/** Request identity sent to the AI chat server: a W3C trace context plus client identity headers. */

type FillRandom = (bytes: Uint8Array) => Uint8Array;

export type TraceContext = { traceId: string; spanId: string; traceparent: string };

export type ClientIdentity = { sessionId: string; version: string | undefined };

const defaultFillRandom: FillRandom = bytes => crypto.getRandomValues(bytes);

const toHex = (bytes: Uint8Array) => Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');

/** Random lowercase hex of `byteLength` bytes; the W3C spec forbids all-zero trace and span IDs. */
function randomNonZeroHex(byteLength: number, fillRandom: FillRandom): string {
	for (;;) {
		const bytes = fillRandom(new Uint8Array(byteLength));
		if (bytes.some(byte => byte !== 0)) return toHex(bytes);
	}
}

export function createTraceContext(fillRandom: FillRandom = defaultFillRandom): TraceContext {
	const traceId = randomNonZeroHex(16, fillRandom);
	const spanId = randomNonZeroHex(8, fillRandom);
	return { traceId, spanId, traceparent: `00-${traceId}-${spanId}-01` };
}

/** RFC 4122 version 4 UUID; `crypto.randomUUID` is unavailable outside secure contexts. */
export function createRandomUuid(fillRandom: FillRandom = defaultFillRandom): string {
	const bytes = fillRandom(new Uint8Array(16));
	// Version 4 in the high nibble of byte 6, variant 10 in the top bits of byte 8.
	bytes[6] = 0x40 + (bytes[6] % 0x10);
	bytes[8] = 0x80 + (bytes[8] % 0x40);
	const hex = toHex(bytes);
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The caller's headers plus the trace and client identity headers. */
export function withAiServerHeaders(
	callerHeaders: HeadersInit | undefined,
	trace: TraceContext,
	client: ClientIdentity
): Headers {
	const headers = new Headers(callerHeaders);
	headers.set('traceparent', trace.traceparent);
	headers.set('x-client-session-id', client.sessionId);
	if (client.version) headers.set('x-client-version', client.version);
	return headers;
}

/** URL path without origin, query string or fragment, safe for debug info and Sentry. */
export function toRequestPath(url: string): string {
	try {
		return new URL(url, 'http://relative.invalid').pathname;
	} catch {
		return url.split(/[?#]/)[0];
	}
}

/** The run a request addresses, when its path names one. */
export function runIdFromPath(path: string): string | undefined {
	const match = /\/ui-chat-(?:runs|stream)\/([^/]+)/.exec(path);
	return match ? decodeURIComponent(match[1]) : undefined;
}
